/**
 * Live tool-input streaming — pure helpers.
 *
 * During `state === 'input-streaming'` the AI SDK progressively parses the
 * model's partial JSON, so a tool part's `input` is a GROWING object: fields
 * appear as their JSON value closes, and the last string field grows
 * character-by-character. The SDK already unescapes JSON string values, so the
 * body we read here is plain text (no half `\n` / `é` escapes to handle).
 *
 * This module decides, per tool, WHICH input field is the streamable "body"
 * (the file/code content the user wants to watch appear) and WHICH is the
 * "title" (usually a file path), plus derives a language for the monospace
 * view. Kept framework-agnostic + pure so it is trivially unit-testable.
 */

export interface StreamingToolInputConfig {
  /** Candidate body fields, in priority order. First non-empty string wins. */
  bodyFields: string[];
  /** Candidate title fields (file path / name), in priority order. */
  titleFields?: string[];
  /** Fallback language for the code view when the title has no file extension. */
  language?: string;
  /** Human label for the header when no title field has parsed yet. */
  label?: string;
}

/**
 * Per-tool config map: tool name → which input field streams as the body and
 * which carries the title. Deliberately data, not scattered conditionals, so
 * adding a tool is a one-line change and the wiring stays generic.
 *
 * NOTE: `html` / `svg` are intentionally absent — they already have dedicated
 * RENDERED live previews (StreamingHtmlPreview / StreamingSvgPreview) that are
 * better than a code view. This map is for code/file/document writers.
 */
export const STREAMING_TOOL_INPUT_CONFIG: Record<string, StreamingToolInputConfig> = {
  // --- The canonical "watch the file being written" writers ---
  writeGalexiaTextFile: { bodyFields: ['content'], titleFields: ['path'], label: 'Writing file' },
  sandboxWriteFile: { bodyFields: ['content'], titleFields: ['path'], label: 'Writing file' },
  browserWriteFile: { bodyFields: ['content'], titleFields: ['path'], label: 'Writing file' },
  replaceGalexiaFileText: {
    bodyFields: ['replace'],
    titleFields: ['pathOrId', 'path'],
    label: 'Editing file',
  },

  // --- Code / document / app builders ---
  htmlNative: { bodyFields: ['html'], titleFields: ['title'], language: 'html', label: 'Writing HTML' },
  remotion: {
    bodyFields: ['componentCode'],
    titleFields: ['title'],
    language: 'tsx',
    label: 'Writing Remotion component',
  },
  hyperframes: {
    bodyFields: ['htmlDocument'],
    titleFields: ['title'],
    language: 'html',
    label: 'Composing HyperFrames document',
  },
  mermaid: { bodyFields: ['code'], titleFields: ['title'], language: 'mermaid', label: 'Drawing diagram' },
};

const EXT_LANGUAGE: Record<string, string> = {
  ts: 'typescript', tsx: 'tsx', js: 'javascript', jsx: 'jsx', mjs: 'javascript', cjs: 'javascript',
  json: 'json', md: 'markdown', mdx: 'markdown', css: 'css', scss: 'scss', less: 'less',
  html: 'html', htm: 'html', xml: 'xml', svg: 'svg', vue: 'vue', svelte: 'svelte',
  py: 'python', rb: 'ruby', go: 'go', rs: 'rust', java: 'java', kt: 'kotlin', c: 'c', h: 'c',
  cpp: 'cpp', cc: 'cpp', hpp: 'cpp', cs: 'csharp', php: 'php', swift: 'swift', sh: 'bash',
  bash: 'bash', zsh: 'bash', yml: 'yaml', yaml: 'yaml', toml: 'toml', sql: 'sql', txt: 'text',
};

/** Derive a highlight language from a file path's extension, if any. */
export function languageFromPath(path: string | undefined): string | undefined {
  if (!path || typeof path !== 'string') return undefined;
  const clean = path.split(/[?#]/)[0];
  const base = clean.slice(clean.lastIndexOf('/') + 1);
  const dot = base.lastIndexOf('.');
  if (dot <= 0) return undefined;
  const ext = base.slice(dot + 1).toLowerCase();
  return EXT_LANGUAGE[ext];
}

function firstString(
  input: Record<string, unknown> | undefined,
  fields: string[] | undefined,
): string | undefined {
  if (!input || !fields) return undefined;
  for (const f of fields) {
    const v = input[f];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return undefined;
}

export interface StreamingToolBody {
  content: string;
  title?: string;
  language?: string;
  label?: string;
}

/**
 * Resolve the streamable body for a tool part's partial input. Returns null
 * when the tool isn't a streamable writer OR its body field hasn't started
 * arriving yet (so callers can fall through to the normal loader).
 *
 * Partial-input safe: every field may be undefined mid-stream; a title that
 * hasn't parsed yet simply yields `title: undefined`.
 */
export function pickStreamingToolBody(
  toolName: string,
  input: unknown,
): StreamingToolBody | null {
  const config = STREAMING_TOOL_INPUT_CONFIG[toolName];
  if (!config) return null;
  const record =
    input && typeof input === 'object' && !Array.isArray(input)
      ? (input as Record<string, unknown>)
      : undefined;
  const content = firstString(record, config.bodyFields);
  if (content === undefined) return null;
  const title = firstString(record, config.titleFields);
  const language = languageFromPath(title) ?? config.language;
  return { content, title, language, label: config.label };
}

export interface CappedLines {
  /** The (possibly trimmed) text to render. */
  text: string;
  /** Total line count of the full content. */
  totalLines: number;
  /** How many leading lines were dropped from `text` (0 when nothing capped). */
  hiddenLines: number;
}

/**
 * Cap a large streaming body to its last `maxLines` lines so the live view
 * stays a bounded, fixed-height region no matter how big the file gets. The
 * full content still renders in the normal card once input-available.
 */
export function capToLastLines(content: string, maxLines: number): CappedLines {
  if (!content) return { text: '', totalLines: content ? 1 : 0, hiddenLines: 0 };
  // Count newlines without allocating a giant array up front.
  let newlines = 0;
  for (let i = 0; i < content.length; i++) {
    if (content.charCodeAt(i) === 10) newlines++;
  }
  const totalLines = newlines + 1;
  if (totalLines <= maxLines) {
    return { text: content, totalLines, hiddenLines: 0 };
  }
  // Walk back from the end to find the start of the last `maxLines` lines.
  let seen = 0;
  let idx = content.length;
  for (let i = content.length - 1; i >= 0; i--) {
    if (content.charCodeAt(i) === 10) {
      seen++;
      if (seen === maxLines) {
        idx = i + 1;
        break;
      }
    }
  }
  return { text: content.slice(idx), totalLines, hiddenLines: totalLines - maxLines };
}

/**
 * Follow-scroll flag: are we close enough to the bottom to keep auto-following
 * the streaming tail? Once the user scrolls up past `threshold` we stop
 * following; when they scroll back to within `threshold` we re-engage. Pure so
 * the scroll-follow logic is unit-testable without a real layout.
 */
export function isPinnedToBottom(
  scrollHeight: number,
  scrollTop: number,
  clientHeight: number,
  threshold: number,
): boolean {
  return scrollHeight - scrollTop - clientHeight <= threshold;
}

const MAX_LIVE_ARGS_CHARS = 1_600;

/**
 * Compact pretty-JSON of a partial tool input for the generic live pending
 * preview — every tool feels alive, not just the writers. Capped so a large
 * partial payload can't blow out the card.
 */
export function formatLiveArgsJson(input: unknown): string | undefined {
  if (input === undefined || input === null) return undefined;
  if (typeof input === 'object' && !Array.isArray(input) && Object.keys(input).length === 0) {
    return undefined;
  }
  let serialized: string;
  try {
    serialized = typeof input === 'string' ? input : JSON.stringify(input, null, 2);
  } catch {
    return undefined;
  }
  if (!serialized || !serialized.trim()) return undefined;
  if (serialized.length <= MAX_LIVE_ARGS_CHARS) return serialized;
  return `${serialized.slice(0, MAX_LIVE_ARGS_CHARS)}\n…`;
}
